import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { peso, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const PIE_COLORS = ["#2563EB", "#16A34A", "#EA580C", "#7C3AED", "#DC2626"];

export default function Dashboard() {
  const { user, hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState({ announcements: [], events: [] });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/announcements").then(({ data }) => setFeed(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400 animate-pulse">Loading dashboard…</div>;

  const p = data.personal || {};
  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="uemp-fade-in">
      <PageHeader
        title={`${greeting}, ${user?.full_name?.split(" ")[0]}`}
        subtitle={`${user?.role} · ${user?.department || "—"}`}
        actions={
          <Button onClick={() => navigate("/leave")} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-file-leave">
            <Icons.CalendarPlus className="h-4 w-4 mr-1.5" /> File Leave
          </Button>
        }
      />

      {/* Company announcements & upcoming events/training */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 min-w-0" data-testid="dash-announcements">
          <div className="flex items-center gap-2 mb-4">
            <Icons.Megaphone className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-slate-800">Announcements & Updates</span>
          </div>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {feed.announcements.map((a) => (
              <div key={a.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0 min-w-0" data-testid="announcement-item">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 text-[10px] capitalize shrink-0">{a.type}</Badge>
                  <span className="text-sm font-medium text-slate-800 truncate">{a.title}</span>
                </div>
                {a.body && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.body}</p>}
                <div className="text-[11px] text-slate-400 mt-1">{fmtDate(a.publish_date || a.created_at)}</div>
              </div>
            ))}
            {feed.announcements.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center" data-testid="announcements-empty">No announcements right now.</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 min-w-0" data-testid="dash-events">
          <div className="flex items-center gap-2 mb-4">
            <Icons.CalendarClock className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-slate-800">Upcoming Events & Training</span>
          </div>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {feed.events.map((ev) => (
              <div key={ev.id} className="flex gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0" data-testid="event-item">
                <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-700 flex flex-col items-center justify-center shrink-0">
                  <span className="text-[9px] uppercase leading-none">{ev.event_date ? new Date(ev.event_date).toLocaleString("en-US", { month: "short" }) : "—"}</span>
                  <span className="text-base font-semibold leading-none mt-0.5">{ev.event_date ? new Date(ev.event_date).getDate() : ""}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px] capitalize">{ev.type}</Badge>
                    <span className="text-sm font-medium text-slate-800 truncate">{ev.title}</span>
                  </div>
                  {ev.location && <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Icons.MapPin className="h-3 w-3" /> {ev.location}</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{ev.event_date ? fmtDate(ev.event_date) : ""}</div>
                </div>
              </div>
            ))}
            {feed.events.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center" data-testid="events-empty">No upcoming events or training.</div>
            )}
          </div>
        </div>
      </div>
      {data.executive && (
        <div className="mb-6">
          <div className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-3">Executive Overview</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
            <StatCard testId="exec-headcount" icon={Icons.Users} label="Headcount" value={data.executive.headcount} accent="blue" sub={`${data.executive.companies} companies`} />
            <StatCard testId="exec-payroll" icon={Icons.Wallet} label="Monthly Payroll" value={peso(data.executive.monthly_payroll)} accent="green" sub="June 2026 net" />
            <StatCard testId="exec-approvals" icon={Icons.ClipboardCheck} label="Pending Approvals" value={data.executive.pending_approvals} accent="amber" sub="Across platform" />
            <StatCard testId="exec-companies" icon={Icons.Building2} label="Companies" value={data.executive.companies} accent="purple" sub="Active entities" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-sm font-medium text-slate-800 mb-4">Headcount by Company</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.executive.headcount_by_company}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="company" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip />
                  <Bar dataKey="headcount" fill="#2563EB" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-sm font-medium text-slate-800 mb-4">Headcount by Department</div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={data.executive.headcount_by_department} dataKey="count" nameKey="department" cx="50%" cy="50%" outerRadius={90} label={(e) => e.department}>
                    {data.executive.headcount_by_department.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Manager */}
      {data.manager && (
        <div className="mb-6">
          <div className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-3">Team Overview</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
            <StatCard testId="mgr-team" icon={Icons.Users} label="Team Members" value={data.manager.team_count} accent="blue" />
            <StatCard testId="mgr-present" icon={Icons.Clock} label="Present Today" value={`${data.manager.present_today ?? 0}/${data.manager.team_count}`} accent="green" sub="Team attendance" />
            <StatCard testId="mgr-pending-leave" icon={Icons.CalendarClock} label="Pending Leave" value={data.manager.pending_team_leave} accent="amber" sub="Awaiting your approval" />
          </div>
          {data.manager.team_attendance?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 mt-4 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 text-sm font-medium text-slate-800 flex items-center justify-between">
                <span>Team Attendance — Today</span>
                <button onClick={() => navigate("/attendance")} className="text-xs text-blue-600 hover:underline" data-testid="view-attendance-link">View all</button>
              </div>
              <div className="divide-y divide-slate-100">
                {data.manager.team_attendance.map((t, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-7 w-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-semibold">{t.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}</div>
                      <div><div className="text-sm text-slate-800">{t.name}</div><div className="text-xs text-slate-400">{t.position}</div></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-slate-500">{t.clock_in ? new Date(t.clock_in).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                      <Badge className={`text-[10px] ${t.status === "Present" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} hover:opacity-100`}>{t.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.manager.team.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 mt-4 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 text-sm font-medium text-slate-800">Direct Reports</div>
              <div className="divide-y divide-slate-100">
                {data.manager.team.map((t) => (
                  <div key={t.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/hris/${t.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold">
                        {t.first_name[0]}{t.last_name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{t.first_name} {t.last_name}</div>
                        <div className="text-xs text-slate-500">{t.position}</div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">{t.employee_no}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Personal */}
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-3">My Workspace</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <StatCard testId="my-tasks-stat" icon={Icons.CheckSquare} label="Pending Tasks" value={p.pending_tasks} accent="amber" />
        <StatCard testId="my-notif-stat" icon={Icons.Bell} label="Unread Alerts" value={p.unread_notifications} accent="rose" />
        <StatCard testId="my-payslip-stat" icon={Icons.Wallet} label="Latest Net Pay" value={p.latest_payslip ? peso(p.latest_payslip.net_pay) : "—"} accent="green" sub={p.latest_payslip?.period_label} />
        <StatCard testId="my-leave-stat" icon={Icons.CalendarDays} label="Leave Types" value={(p.leave_balances || []).length} accent="blue" sub="Available" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm font-medium text-slate-800 mb-4">Leave Balances</div>
          <div className="space-y-4">
            {(p.leave_balances || []).map((b) => (
              <div key={b.id}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-700">{b.leave_type}</span>
                  <span className="text-slate-500">{b.balance} / {b.entitled} days</span>
                </div>
                <Progress value={(b.balance / b.entitled) * 100} className="h-2" />
              </div>
            ))}
            {(p.leave_balances || []).length === 0 && <div className="text-sm text-slate-400">No leave balances.</div>}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm font-medium text-slate-800 mb-4">Quick Actions</div>
          <div className="space-y-2">
            {[
              ["File Leave", Icons.CalendarPlus, "/leave"],
              ["View Payslip", Icons.Receipt, "/payroll"],
              ["My Tasks", Icons.CheckSquare, "/tasks"],
              ["My Profile", Icons.User, "/profile"],
              ...(hasPerm("employee.view") ? [["Employee Directory", Icons.Users, "/hris"]] : []),
            ].map(([label, Icon, path]) => (
              <button key={label} onClick={() => navigate(path)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-sm text-slate-700">
                <Icon className="h-4 w-4 text-blue-600" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
